/**
 * SanctaChatContext — a React context that provides all STABLE state
 * needed by the Sancta widget header (chatbot config, language, detection,
 * toggles, handlers).
 *
 * Why a separate context (not the useSanctaThread hook result)?
 * ─ Every streaming token causes `messages` state to change, which forces
 *   the SanctaChatbot parent to re-render. Passing 20+ props to
 *   SanctaHeader on every re-render was wasteful (header re-computes
 *   its language menu, drag styles, etc. every ~30 ms during a stream).
 * ─ By putting HEADER-only state in its own context, and wrapping the
 *   header in `React.memo`, we make the header re-render only when its
 *   own slice (language, detection, tts.speaking) genuinely changes —
 *   not on every streamed word.
 *
 * We DO NOT put `messages`, `input`, or `streaming` in this context.
 * Those live in useSanctaThread and are passed explicitly to the body
 * / input components that actually need them.
 */
import React, { createContext, useContext, useMemo } from "react";

type Detection = {
  code: string;
  confidence: number;
  source: string;
} | null;

export interface SanctaChatContextValue {
  // Chatbot config (name, accent, tagline)
  chatbotName: string;
  chatbotAccent: string;
  tagline: string;                     // already interpolated

  // Layout
  variant: "widget" | "page";
  isMobile: boolean;

  // Localization
  activeLabel: string;
  langCode: string;
  setLangCode: (c: string) => void;
  langNativeName: string;
  langPickerOpen: boolean;
  setLangPickerOpen: (o: boolean) => void;

  // Detection (updates once per assistant turn)
  detection: Detection;

  // Toggles
  autoSwap: boolean;
  setAutoSwap: (b: boolean) => void;
  autoRead: boolean;
  setAutoRead: (b: boolean) => void;
  voiceReplyOn: string;
  voiceReplyOff: string;
  voiceReplyHint: string;

  // TTS state — only `speaking` is exposed to the header (drives the
  // pulsing avatar). The rest lives inside SanctaMessage.
  ttsSpeaking: boolean;
  ttsSupported: boolean;

  // Auth-gate for user-facing buttons
  showUserActions: boolean;

  // Header handlers (stable references)
  onExpand: () => void;
  onMemoryOpen: () => void;
  onClose: () => void;

  // Drag handle spread — for the header's drag zone in widget mode
  widgetDragProps?: any;
}

const SanctaChatCtx = createContext<SanctaChatContextValue | null>(null);

export const SanctaChatProvider: React.FC<{
  value: SanctaChatContextValue;
  children: React.ReactNode;
}> = ({ value, children }) => {
  // Memoize the object identity ourselves — SanctaChatbot passes fresh
  // values each render, but only the referenced *fields* change. Keying
  // the memo on scalar identities means the context value only produces
  // a new object when a real header-relevant field changes.
  const memoized = useMemo(() => value, [
    value.chatbotName,
    value.chatbotAccent,
    value.tagline,
    value.variant,
    value.isMobile,
    value.activeLabel,
    value.langCode,
    value.langNativeName,
    value.langPickerOpen,
    value.detection?.code,
    value.detection?.confidence,
    value.autoSwap,
    value.autoRead,
    value.ttsSpeaking,
    value.ttsSupported,
    value.showUserActions,
    // handlers + setters are assumed stable from parent (defined once)
    value.setLangCode,
    value.setLangPickerOpen,
    value.setAutoSwap,
    value.setAutoRead,
    value.onExpand,
    value.onMemoryOpen,
    value.onClose,
    value.widgetDragProps,
  ]);
  return <SanctaChatCtx.Provider value={memoized}>{children}</SanctaChatCtx.Provider>;
};

export function useSanctaChat(): SanctaChatContextValue {
  const ctx = useContext(SanctaChatCtx);
  if (!ctx) {
    throw new Error("useSanctaChat must be used inside <SanctaChatProvider>");
  }
  return ctx;
}
